import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';
import type { DemoAdminUser, ShareableType } from '../lib/types.js';
import { Modal } from './Modal.js';
import { useToast } from './Toast.js';

/**
 * Picks which demo administrators see an item. A demo administrator only sees what it created and
 * what's shared with it here (backend `ResourceGrantController`); admins with `users.manage` only.
 */
export function ShareWithDemoAdmins({
  type,
  id,
  name,
  onClose,
}: {
  type: ShareableType;
  id: string;
  name: string;
  onClose: () => void;
}): ReactNode {
  const toast = useToast();
  const [admins, setAdmins] = useState<DemoAdminUser[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([api.demoAdmins(), api.shares(type, id)])
      .then(([list, userIds]) => {
        setAdmins(list);
        setSelected(new Set(userIds));
      })
      .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : String(err)));
  }, [type, id]);

  function toggle(userId: number): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await api.setShares(type, id, [...selected]);
      toast.push('Sharing updated', 'success');
      onClose();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Share “${name}”`} onClose={onClose}>
      <div className="stack">
        <p className="muted small">
          Demo administrators only see what they created and what&apos;s shared with them here.
        </p>
        {loadError !== null ? <div className="form-error">{loadError}</div> : null}
        {admins === null && loadError === null ? <p className="faint small">Loading…</p> : null}
        {admins !== null && admins.length === 0 ? (
          <p className="faint small">
            No demo administrators yet. Give a user the Demo administrator role under Settings → Users.
          </p>
        ) : null}
        {admins !== null && admins.length > 0 ? (
          <div className="card" style={{ maxHeight: 280, overflowY: 'auto', padding: '4px 10px' }}>
            {admins.map((admin) => (
              <label key={admin.id} className="row" style={{ padding: '4px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.has(admin.id)} onChange={() => toggle(admin.id)} />
                <span>{admin.name}</span>
                <span className="faint small">{admin.email}</span>
              </label>
            ))}
          </div>
        ) : null}
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || admins === null} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
